import {
    LoanDetail, LoanDocument, LoanPurpose, NumUnits, OccupancyType, PropertyType, State
} from '@api';
import {
    createDate, getRandomEnumValue, getRandomItemFromArray, randomBoolean, randomNum
} from '@tsp-ui/core/utils';
import {
    DefaultBodyType, ResponseResolver, RestContext, RestRequest, rest
} from 'msw';

import { getMockUrl } from '../../../mocks/getMockUrl';
import { SAMPLE_PDF_URL } from '../../customer/due-diligence-step/due-diligence-step-mocks';
import { documentTypes } from '../../document-type/document-type-mocks';
import { getLoanDetail } from '../loan-mocks';


let loanDocumentID = 400;

type MswHandler = ResponseResolver<
    RestRequest<
        Record<string, any>,
        Record<string, string>
    >,
    RestContext,
    DefaultBodyType
>;

const loanToDocsMap: Record<string, LoanDocument[]> = {};
const docToSubmissionMap: Record<string, any> = {};
const docToOcrDataMap: Record<string, Partial<LoanDetail>> = {};

const getLoanDocumentsHandler: MswHandler = (req, res, ctx) => res(
    ctx.status(200),
    ctx.delay(500),
    ctx.json(getLoanDocuments(req.params.loanID))
);

const getLoanDocumentsOCRDataHandler: MswHandler = (req, res, ctx) => res(
    ctx.status(200),
    ctx.delay(500),
    ctx.json(getOCRDataForLoanDocument(req.params.loanID, req.params.documentId))
);

const getLoanDocumentUrlHandler: MswHandler = (req, res, ctx) => res(
    ctx.status(200),
    ctx.delay(1000),
    ctx.json({
        preSignedUrl: SAMPLE_PDF_URL,
        expiration: createDate(.001).toISOString() // 1.44 minutes from now
    })
);

const uploadLoanDocumentHandler: MswHandler = (req, res, ctx) => res(
    ctx.status(200),
    ctx.json<LoanDocument>({
        id: String(loanDocumentID++),
        loanId: req.params.loanID,
        name: req.body.file.name,
        containerName: 'Unnamed Container',
        instanceCount: 1,
        url: SAMPLE_PDF_URL,
        uploadedAt: '',
        uploadedByUserId: '1'
    })
);

const getOCRSubmissionDataHandler: MswHandler = (req, res, ctx) => {
    const submission = docToSubmissionMap[req.params.documentId] || undefined;

    return res(
        ctx.status(200),
        ctx.delay(500),
        ctx.json(submission)
    );
};

const updateOCRSubmissionDataHandler: MswHandler = (req, res, ctx) => {
    docToSubmissionMap[req.params.documentId] = req.body;

    return res(
        ctx.status(200),
        ctx.delay(500),
        ctx.json(req.body)
    );
};

export const mocks = [
    rest.get(getMockUrl('/client/:clientID/loan/:loanID/document'), getLoanDocumentsHandler),
    rest.get(getMockUrl('/client/:clientID/loan/:loanID/document/:documentId/ocr-data'), getLoanDocumentsOCRDataHandler),
    rest.get(getMockUrl('/client/:clientID/customer/:customerId/loan/:loanID/document/:documentId/ocr-data'), getLoanDocumentsOCRDataHandler),
    rest.get(getMockUrl('/client/:clientID/loan/:loanID/document/:documentId/ocr-submission'), getOCRSubmissionDataHandler),
    rest.get(getMockUrl('/client/:clientID/customer/:customerId/loan/:loanID/document/:documentId/ocr-submission'), getOCRSubmissionDataHandler),
    rest.put(getMockUrl('/client/:clientID/loan/:loanID/document/:documentId/ocr-submission'), updateOCRSubmissionDataHandler),
    rest.put(getMockUrl('/client/:clientID/customer/:customerId/loan/:loanID/document/:documentId/ocr-submission'), updateOCRSubmissionDataHandler),
    rest.get(getMockUrl('/client/:clientID/customer/:customerId/loan/:loanID/document'), getLoanDocumentsHandler),
    rest.get(getMockUrl('/client/:clientID/loan/:loanID/document/:documentId'), getLoanDocumentUrlHandler),
    rest.get(getMockUrl('/client/:clientID/customer/:customerId/loan/:loanID/document/:documentId'), getLoanDocumentUrlHandler),
    rest.post(getMockUrl('/client/:clientID/loan/:loanID/document'), uploadLoanDocumentHandler)
];

function getLoanDocuments(loanId: string): LoanDocument[] {
    if (loanToDocsMap[loanId]) {
        return loanToDocsMap[loanId];
    }

    const loanDocs = [
        ...(randomBoolean()
            ? []
            : Array(randomNum(1, 12)))
    ].map<LoanDocument>(() => {
        // eslint-disable-next-line max-len
        const docType = getRandomItemFromArray(documentTypes.slice(0, 15)); // the first fifteen are also used for required docs, so this makes sure that these docs line up with the required docs

        const loanDoc: LoanDocument = {
            id: String(loanDocumentID++),
            loanId,
            name: docType.name,
            containerName: docType.containerLabel,
            instanceCount: 1,
            url: getMockUrl('/document/loan/someLoanID/someContainer/someFilename'),
            uploadedAt: '2024-08-19T12:30:00Z',
            uploadedByUserId: '1',
            isReviewed: false
        };

        return loanDoc;
    }).reduce((documents, currentDoc) => { // increment instanceCount on nonunique docNames
        const duplicate = documents.find(({ name }) => name === currentDoc.name);

        if (duplicate) {
            currentDoc.id = duplicate.id;
            currentDoc.instanceCount += 1;
        }

        return documents.concat([ currentDoc ]);
    }, [] as LoanDocument[]);

    loanToDocsMap[loanId] = loanDocs;
    return loanDocs;
}

function getOCRDataForLoanDocument(loanId: string, documentId: string): Partial<LoanDetail> {
    if (docToOcrDataMap[documentId]) {
        return docToOcrDataMap[documentId];
    }

    const numFieldsToInclude = randomNum(3, 8);
    const possibleFields: Array<keyof LoanDetail> = [
        'loanAmount', 'interestRate', 'loanTerm', 'borrowerName',
        'propertyType', 'purpose', 'appraisedValue', 'salePrice',
        'escrowsFlag', 'interestOnlyFlag', 'occupancy', 'units',
        'armMargin', 'subordinatedBalance', 'cashOutAmount'
    ];

    // Randomly select which fields to include
    const shuffledFields = [ ...possibleFields ].sort(() => Math.random() - 0.5);
    const selectedFields = shuffledFields.slice(0, numFieldsToInclude);

    const partialLoanDetail: Partial<LoanDetail> = {
        id: loanId
    };

    // Add random values for selected fields
    selectedFields.forEach(field => {
        switch (field) {
        case 'loanAmount':
            partialLoanDetail.loanAmount = randomNum(50000, 1000000);
            break;
        case 'interestRate':
            partialLoanDetail.interestRate = randomNum(2, 7) + Math.random();
            break;
        case 'loanTerm':
            partialLoanDetail.loanTerm = getRandomItemFromArray([ 15, 30 ]);
            break;
        case 'borrowerName':
            partialLoanDetail.borrowerName = getRandomItemFromArray([
                'John Smith', 'Maria Garcia', 'James Johnson', 'Linda Williams',
                'Robert Brown', 'Patricia Davis', 'Michael Miller'
            ]);
            break;
        case 'propertyType':
            partialLoanDetail.propertyType = getRandomEnumValue(PropertyType);
            break;
        case 'purpose':
            partialLoanDetail.purpose = getRandomEnumValue(LoanPurpose);
            break;
        case 'appraisedValue':
            partialLoanDetail.appraisedValue = randomNum(100000, 1500000);
            break;
        case 'salePrice':
            partialLoanDetail.salePrice = randomNum(100000, 1500000);
            break;
        case 'escrowsFlag':
            partialLoanDetail.escrowsFlag = randomBoolean();
            break;
        case 'interestOnlyFlag':
            partialLoanDetail.interestOnlyFlag = randomBoolean();
            break;
        case 'occupancy':
            partialLoanDetail.occupancy = getRandomEnumValue(OccupancyType);
            break;
        case 'units':
            partialLoanDetail.units = getRandomEnumValue(NumUnits);
            break;
        case 'armMargin':
            partialLoanDetail.armMargin = randomNum(1, 5) + Math.random();
            break;
        case 'subordinatedBalance':
            partialLoanDetail.subordinatedBalance = randomNum(10000, 200000);
            break;
        case 'cashOutAmount':
            partialLoanDetail.cashOutAmount = randomNum(5000, 100000);
            break;
        default:
            break;
        }
    });

    // Add random address data
    if (selectedFields.includes('propertyType')) {
        partialLoanDetail.address = {
            street: `${randomNum(100, 9999)} ${getRandomItemFromArray([
                'Main', 'Oak', 'Maple', 'Cedar', 'Pine'
            ])} ${getRandomItemFromArray([
                'St', 'Ave', 'Blvd', 'Dr', 'Ln'
            ])}`,
            city: getRandomItemFromArray([
                'Springfield', 'Riverside', 'Franklin', 'Greenville', 'Clinton'
            ]),
            state: getRandomEnumValue(State),
            zip: `${randomNum(10000, 99999)}`
        };
    }

    const existingLoanDetail = getLoanDetail(loanId);
    const existingBorrowerIds = existingLoanDetail.borrowers.map(b => b.id);
    const newBorrowerId = randomNum(1000, 9999).toString();

    partialLoanDetail.borrowers = [
        ...(randomBoolean()
            ? [
                {
                    id: existingBorrowerIds[0],
                    firstName: existingLoanDetail.borrowerName!.split(' ')[0],
                    lastName: existingLoanDetail.borrowerName!.split(' ')[1],
                    ssn: `${randomNum(100000000, 999999999)}`
                }
            ]
            : []),
        ...(randomBoolean()
            ? [
                {
                    id: newBorrowerId,
                    firstName: getRandomItemFromArray([
                        'John', 'Maria', 'James', 'Linda', 'Robert', 'Patricia', 'Michael'
                    ]),
                    lastName: getRandomItemFromArray([
                        'Smith', 'Garcia', 'Johnson', 'Williams', 'Brown', 'Davis', 'Miller'
                    ]),
                    ssn: `${randomNum(100000000, 999999999)}`
                }
            ]
            : [])
    ];

    docToOcrDataMap[documentId] = partialLoanDetail;

    return partialLoanDetail;
}
